import get from 'lodash.get';
import { GuidService } from 'ui-components/services/guid-service';

import { PusherService } from '../pusher-service';
import { ChannelAuthorizationHelpers } from '../pusher-service/helpers';
import * as Actions from '../../store/shipstation-connect/ss-connect.actions';
import * as AutoActions from '../../store/shipstation-connect/auto-scales/auto-scale.actions';
import { noop } from 'ui-components/default-props/noop';

const mapRawScaleFields = (scale, workstationId) => {
  return {
    // there is no guarantee scaleId's are unique
    id: `${scale.i}-${workstationId}`,
    idFromConnect: scale.i,
    name: scale.n,
    shared: scale.s === 1,
    disabled: scale.d === 1,
    ssTimeout: scale.st,
    deviceTimeout: scale.dt,
    weight1Byte: scale.w1b,
    weight2Byte: scale.w2b,
    weight1Factor: scale.w1f,
    weight2Factor: scale.w2f,
    readWait: scale.rw,
    retries: scale.r,
    model: scale.model,
    port: scale.port,
    host: scale.host,
    isDimensionalizer: scale.isDimensionalizer,
    workstationId,
  };
};

const CONNECT_BROADCAST_SCALES = {
  eventName: 'client-connect:broadcast:scales',
  actionToDispatch: (pusherMessage) => {
    const { scales, workstationId } = pusherMessage;
    const humanFriendlyScales = scales.map((scale) => mapRawScaleFields(scale, workstationId));
    return Actions.updateScalesSuccess(workstationId, humanFriendlyScales);
  },
  shouldIgnoreNotForMe: true,
};

export const convertConnectWeightToOunces = (lbs, oz) => lbs * 16 + oz;

export const CONNECT_AUTO_READ_SCALE_SUCCESS = {
  eventName: 'client-connect:auto:scale:reading',
  actionToDispatch: (message) => {
    // TODO support metric
    const { lbs, oz, dims } = message.weight;
    const weight = convertConnectWeightToOunces(lbs, oz);
    return AutoActions.scaleReadSuccess({ weight, dimensions: dims });
  },
};

// TODO: SPA-564
const CONNECT_AUTO_READ_SCALE_FAILURE = {
  eventName: 'client-connect:auto:scale:error',
  actionToDispatch: () => {
    return {};
  },
};

// TODO: SPA-564
const CONNECT_RESPONSE_SCALE_READ = {
  eventName: 'client-connect:response:scale:read',
  actionToDispatch: (pusherMessage) => {
    if (!get(pusherMessage, 'weight')) {
      return;
    }
    // TODO support metric
    const { requestId, workstationId, dims } = pusherMessage;
    const { lbs, oz } = pusherMessage.weight;
    const weight = convertConnectWeightToOunces(lbs, oz);
    return Actions.readScaleResponse(weight, dims, requestId, workstationId);
  },
};

export const AutoReadScaleEvents = [CONNECT_AUTO_READ_SCALE_SUCCESS, CONNECT_AUTO_READ_SCALE_FAILURE];

export const ConnectScalesEvents = [CONNECT_BROADCAST_SCALES, CONNECT_RESPONSE_SCALE_READ];

class ConnectScalesClass {
  readFromScale(scale, requestId) {
    const pusherArguments = {
      requestId: requestId || GuidService.newGuid(),
      workstationId: scale.workstationId,
      scaleId: scale.idFromConnect,
    };

    PusherService.push(
      'connect:request:scale:read',
      pusherArguments,
      PusherService.getWorkstationChannelName(pusherArguments.workstationId),
    );
  }

  autoReadChannelName(sellerId, scale) {
    const channelSuffix = [sellerId, scale.workstationId, scale.idFromConnect].join('_');
    return `presence-${channelSuffix}`;
  }

  convertActionToObserverWithScaleId(scaleId, action, observer) {
    return (message) => {
      const messagePayload = {
        ...message,
        scaleId,
      };
      observer.next(action(messagePayload));
    };
  }

  startAutoReadFromScale(sellerId, scale, observer) {
    const channelName = this.autoReadChannelName(sellerId, scale);
    // always allow the auto scale channel to authorize
    ChannelAuthorizationHelpers.removeChannelSocketName(channelName);
    PusherService.subscribeToChannel(channelName, noop);
    AutoReadScaleEvents.forEach((event) => {
      const { eventName, actionToDispatch } = event;
      const dispatchWithScaleId = this.convertActionToObserverWithScaleId(scale.id, actionToDispatch, observer);
      PusherService.bindToEvent(eventName, dispatchWithScaleId, channelName);
    });
  }

  stopAutoReadFromScale(sellerId, scale) {
    const channelName = this.autoReadChannelName(sellerId, scale);
    PusherService.tearDownChannel(channelName);
    AutoReadScaleEvents.forEach((event) => {
      const { eventName } = event;
      // pass null to completely unbind scale channel
      PusherService.unbindFromEvent(eventName, null, channelName);
    });
  }
}

export const ConnectScales = new ConnectScalesClass();
